import React, { useContext } from "react";
import { Switch, Redirect, Route as RouterRoute } from "react-router-dom";
import { LinkedInPopUp } from "react-linkedin-login-oauth2";

import { UserContext, SettingsContext, useEntitlements } from '../contexts/index.jsx';
import { canUseOpenedFeature } from '../contexts/openedFeature';
import { canSubmitCatalogEntries } from '../helpers/adminAccess.js';
import { Route, AuthenticationLayout as Authentication, RCAdminLayout } from "./index.jsx";
import { DashboardPage } from "../dashboard";
import { DASHBOARD_PATH, DASHBOARD_LOCKED, DASHBOARD_FALLBACK } from "../core/config/dashboard.js";
import { RCListingsPage } from "../listings/pages/index.jsx";
import RCLookbooksPage from "../listings/pages/RCLookbooksPage.jsx";
import RequestLookbooksPage from "../requests/lookbook/RequestLookbooksPage.jsx";
import CoreListPage from "../stock/CoreListPage.jsx";
import { ImportStock } from "../onboarding/ImportStock.jsx";

import { CheckoutPage } from '../cart/index.jsx';
import { RequestPage } from "../requests/components/RequestPage";
import { RequestPageV2 } from "../requests/v2/RequestPageV2";
import { RequestCalculationPage } from "../requests/components/RequestCalculationPage";
import {
  ListingsPage,
  LookbooksPage,
  NetworkPage,
  UpsertLookbookPage,
} from "../listings";
import {
  MerchantsPage,
  MerchantPricingTable,
  MerchantSettingsPage,
} from "../merchants";
import { DaoSettingsPage, ShopifySettingsPage, WhatsAppCoexistencePage, CoobyVmPage, WaBroadcastPage, MasterAdminSettingsPage, MasterAdminGovernancePage } from "../settings";
import RCListingCreate from "../views/RCListingCreate.jsx";
import RCStockListingCreate from "../listingFlows/RCStockListingCreate.jsx";
import RCConsignmentListingCreate from "../listingFlows/RCConsignmentListingCreate.jsx";
import {
  CreateAccount,
  Dashboard,
  Error,
  LandingGuest,
  LandingUser,
  Listing,
  ListingCreate,
  ListingEdit,
  Listings,
  Order,
  Orders,
  Requests,
  KanbanRequests,
  PayOrder,
  Page,
  Pages,
  Profile,
  RecoverAccount,
  ResetPassword,
  SignIn,
  Users,
  VerifyEmail,
  OfferConfirmation,
  Leads,
} from "../views";

import TradeFeed from "../views/TradeFeed";
import AcceptInvite from "../views/AcceptInvite";
import AddProduct from "../product/components/AddProduct";
import ProductPreview from "../product/components/ProductPreview";
import { AnalyticsPage } from "../analytics";
import TransactionsPnlPage from "../views/TransactionsPnl/TransactionsPnlPage";
import ReconciliationPage from "../views/Reconciliation/ReconciliationPage";
import MonitoringPage from "../views/Monitoring/MonitoringPage";
import UsageAnalyticsPage from "../views/UsageAnalytics/UsageAnalyticsPage";
import DealsPage from "../views/Deals/DealsPage";
import UsageUserReportPage from "../views/UsageAnalytics/UsageUserReportPage";
import AiPricingView from "../order/components/AiPricing";
import PricingPage from "../views/PricingPage";
import RCAlertCreate from "../buyAlerts/RCAlertCreate";
import RCAlertEdit from "../buyAlerts/RCAlertEdit";
import RCBuyAlertsPage from "../buyAlerts/RCBuyAlertsPage";
import NotificationsPage from "../notifications/NotificationsPage";
import MerchantChatPage from "../merchantChat/MerchantChatPage";
import GoogleCallbackPage from "../views/DailyRecap/GoogleCallbackPage";
import StyleGuidePage from "../styleguide/StyleGuidePage";
import StyleGuideV2Page from "../styleguide/StyleGuideV2Page";
import PaletteExplorerPage from "../styleguide/PaletteExplorerPage";

const Routes = (props) => {
  const userContext = useContext(UserContext);
  const { isRetailCircle } = useContext(SettingsContext);
  const entitlements = useEntitlements();

  const None = ({ content, ...props }) => {
    const Content = content;
    return <Content {...props} />;
  };

  return (
    <Switch>
      {/*
        The `Route` imported above is the project's CustomRoute, which always
        renders via a `render` prop and silently drops children. Use the raw
        react-router Route here so the <Redirect> inside actually fires.
      */}
      {isRetailCircle && (
        <RouterRoute exact path="/">
          {userContext.user
            ? <Redirect to="/requests/orders" />
            : <Redirect to="/sign-in" />}
        </RouterRoute>
      )}
      {!isRetailCircle && (
        <Route
          exact
          path="/"
          withCategories
          withSimpleFooter={false}
          className="homepage"
          content={LandingUser}
          allowShopify
        />
      )}
      {userContext.user && (
        <Route exact path="/member">
          <Redirect to="/" />
        </Route>
      )}
      {!userContext.user && (
        <Route
          exact
          path={["/member"]}
          className="landing"
          content={LandingGuest}
        />
      )}
      {/* RESOLVED (was the OPEN DECISION in fe-dao-admin-sweep report): AddProduct
          POSTs /api/products, i.e. it creates a row in the GLOBAL product
          catalogue every store shares — historically is_master_admin only. Master
          admins can now delegate this via User::ACCESS_CREATE_PRODUCT (master-admin
          settings → Store Admin → Access), the "have another way to get a missing
          product into the catalogue" this was waiting on. Route, the "Create
          Product" button (settings/organisms/DaoSettings.jsx), and the backend
          policy (ProductPolicy::create) all gate on the same capability, so they
          move together. */}
      {/* Store-catalog feature widened this: any store admin/owner may now
          submit entries for THEIR OWN store (approval-gated backend-side);
          masters + delegated CREATE_PRODUCT holders keep writing the shared
          catalogue. Mirrors ProductPolicy::create(). */}
      {canSubmitCatalogEntries(userContext?.user, userContext?.accessibleStores, userContext?.activeStoreId) && (
        <Route
          exact
          path="/add-new-product"
          withCategories
          title="Add new product"
          content={AddProduct}
        />
      )}
      {/* Design-system live map — standalone, no auth/shell. See src/styleguide. */}
      <Route
        exact
        path="/styleguide"
        layout={None}
        content={StyleGuidePage}
      />
      {/* Proposed software design system v2 (website-derived). Standalone. */}
      <Route
        exact
        path="/styleguide-v2"
        layout={None}
        content={StyleGuideV2Page}
      />
      {/* Standalone primary-colour exploration — kept off the v2 page. */}
      <Route
        exact
        path="/styleguide-v2/palette"
        layout={None}
        content={PaletteExplorerPage}
      />
      <Route
        path="/auth/linkedin/redirect"
        layout={None}
        content={LinkedInPopUp}
      />
      <Route
        path="/auth/google/callback"
        layout={None}
        content={GoogleCallbackPage}
      />
      <Route
        path="/checkout/:orderId?"
        withCategories
        content={CheckoutPage}
        narrow
        title="Complete purchase"
        requireUser
        allowLimited
      />
      <Route
        exact
        path="/users/create"
        layout={RCAdminLayout}
        content={Users}
        action="create"
        requireMember
        /* Same reason as /users/:type? below: for action="create" Users.jsx
           renders its own <PageHeader> ("Create New User" + back) — but
           RCAdminLayout's RCTopBar doesn't know that and stacks its own
           "User CRM" bar (with its own back button) above it regardless,
           unless suppressed here. */
        noTopBar
      />
      <Route
        exact
        path="/users/:type?"
        layout={RCAdminLayout}
        content={Users}
        requireMember
        /* Users renders its own <PageHeader>; without this the layout top bar
           would stack a second "User CRM" heading above it. */
        noTopBar
      />
      <Route
        exact
        path="/merchants"
        withCategories
        content={MerchantsPage}
        requireUser
      />
      <Route
        exact
        path="/experts"
        withCategories
        content={MerchantsPage}
        requireUser
      />
      {userContext?.user?.is_merchant_admin && (
        <Route
          exact
          path="/merchants/:uuid/settings"
          withCategories
          content={MerchantSettingsPage}
          requireUser
          allowShopify
        />
      )}
      {/* Self sign-up on the RC web app is retired (2026-10-30): it is
          staff-only, and accounts come from team invites and provisioning.
          The backend refuses sign-up from this origin; this sends any old
          link or bookmark to sign-in instead of a form that cannot succeed. */}
      {isRetailCircle && (
        <RouterRoute path="/create-account">
          <Redirect to="/sign-in" />
        </RouterRoute>
      )}
      <Route
        exact
        path="/create-account/:firstParam?/:secondParam?"
        layout={Authentication}
        content={CreateAccount}
        requireGuest
        allowShopify
        allowRetailCircle
      />
      {/* Earnings — was "Dashboard" at /dashboard until 2026-10-20, when that
          name and URL went to the KPI page (ex-Overview, see RC Admin pages).
          Old sub-tab links (/dashboard/rewards) still land here, query kept
          for the iframe hosts' tokens. */}
      <Route
        exact
        path="/earnings"
        withCategories
        requireMember
        requireFeature="earnings"
        content={Dashboard}
      />
      <Route
        exact
        path="/earnings/:step"
        withCategories
        requireMember
        requireFeature="earnings"
        content={Dashboard}
      />
      <RouterRoute
        exact
        path="/dashboard/:step"
        render={({ match, location }) => (
          <Redirect to={{ pathname: `/earnings/${match.params.step}`, search: location.search }} />
        )}
      />
      <Route
        exact
        path="/error/:code?"
        content={Error}
        className={userContext.user ? "" : "landing"}
        allowShopify
      />
      <Route
        exact
        path="/logout"
        allowUnactivated
        requireUser
        action="logout"
        allowShopify
      />
      <Route
        path="/order/:reference"
        withCategories
        content={Order}
        narrow
        title="Order Confirmation"
        requireUser
        allowLimited
      />
      <Route
        path="/order-payment/:reference/:step?"
        withCategories
        content={PayOrder}
        narrow
        title="Pay Order"
        requireUser
        allowLimited
      />
      <Route
        exact
        path={[
          "/product/:listingId/edit/product",
          "/product/:listingId/edit/listings",
          "/product/:listingId/edit/classify",
          "/product/:listingId/edit/payment",
        ]}
        layout={RCAdminLayout}
        content={ListingEdit}
        requireMember
        allowShopify
        /* RCListingForm renders the standard PageLayout + PageHeader for every
           listing flow. RCAdminLayout's own RCTopBar takes its title from the
           path, not a prop, so without this the flow shows its title twice. */
        noTopBar
      />
      {/* Purpose-built create flows. MUST come before the generic
          /product/create/:baseProductId? route — in a v5 <Switch> the first
          match wins, and the generic route would swallow "stock" /
          "consignment" as a baseProductId. */}
      <Route
        exact
        path="/product/create/stock"
        layout={RCAdminLayout}
        content={RCStockListingCreate}
        requireMember
        /* See the edit route above — RCListingForm owns the chrome. */
        noTopBar
      />
      <Route
        exact
        path="/product/create/consignment"
        layout={RCAdminLayout}
        content={RCConsignmentListingCreate}
        requireMember
        /* See the edit route above — RCListingForm owns the chrome. */
        noTopBar
      />
      <Route
        exact
        path={[
          "/product/create/:baseProductId?",
          "/product/create/:baseProductId/listings",
          "/product/create/:baseProductId/classify",
          "/product/create/:baseProductId/payment",
        ]}
        layout={RCAdminLayout}
        content={RCListingCreate}
        requireMember
        /* See the edit route above — RCListingForm owns the chrome. */
        noTopBar
      />
      <Route
        exact
        path="/product/:id"
        withSearchFilters
        content={Listing}
        allowShopify
      />
      <Route
        exact
        path="/view-product/:viewProductId"
        withCategories
        content={ProductPreview}
        layoutProps={{
          contentProps: { inModal: false },
        }}
        allowShopify
      />
      <Route
        path={["/products/favourite", "/products/wishlist"]}
        withCategories
        content={Listings}
        requireFeature="wishlist"
      />
      <Route
        path="/listings"
        exact
        content={ListingsPage}
        withCategories
        requireMember
        allowShopify
      />
      <Route
        path="/products"
        content={Listings}
        withSearch
        withSearchFilters
        withCategories
        allowShopify
      />
      <Route
        path="/listings/lookbooks"
        layout={RCAdminLayout}
        content={RCLookbooksPage}
        exact
        requireMember
        requireFeature="lookbook_generation"
        noTopBar
      />
      <Route
        path="/listings/network"
        content={NetworkPage}
        exact
        withCategories
        requireMember
        requireFeature="outside_network_search"
      />
      <Route
        path="/lookbooks/:id"
        exact
        layout={RCAdminLayout}
        content={UpsertLookbookPage}
        requireMember
        requireFeature="lookbook_generation"
      />
      <Route
        exact
        path="/orders"
        layout={RCAdminLayout}
        content={Orders}
        requireUser
        allowLimited
        noTopBar
      />
      <Route
        exact
        path={[
          "/alerts/create/:baseProductId?",
          "/alerts/create/:baseProductId/filters",
        ]}
        layout={RCAdminLayout}
        content={RCAlertCreate}
        title="New Buy Alert"
        requireMember
        allowShopify
        noTopBar
      />
      <Route
        exact
        path={["/alerts/:alertId/edit", "/alerts/:alertId/edit/filters"]}
        layout={RCAdminLayout}
        content={RCAlertEdit}
        title="Edit Buy Alert"
        requireMember
        allowShopify
        noTopBar
      />
      <Route
        exact
        path="/alerts"
        layout={RCAdminLayout}
        content={RCBuyAlertsPage}
        title="Buy Alerts"
        requireUser
        allowLimited
        noTopBar
      />
      <Route
        exact
        path="/leads/:orderLeadType"
        layout={RCAdminLayout}
        content={Leads}
        requireUser
        allowLimited
        requireFeature="wa_group_ingestion"
        /* Leads renders its own <PageHeader>; without this the layout top bar
           would stack a second, differently-styled heading above it — the
           same reason /users/:type and /rc/listings pass it. */
        noTopBar
      />
      {/* Trade Feed — the raw WhatsApp intake, upstream of Leads.
          Master admins, and since 2026-10-27 merchants whose store has the
          `trade_feed` row AND is in enforce mode (canUseOpenedFeature — the
          same rule as the API's `feature.open:trade_feed`). The collection
          behind it (`whatsapp_messages`) has no store id, so every merchant
          sees the same messages; Alex's call. */}
      {canUseOpenedFeature(userContext.user, entitlements, "trade_feed") && (
        <Route
          exact
          path="/trade-feed"
          layout={RCAdminLayout}
          content={TradeFeed}
          requireUser
          /* Renders its own <PageHeader>, same as Leads. */
          noTopBar
        />
      )}
      <Route
        exact
        path="/notifications"
        layout={RCAdminLayout}
        content={NotificationsPage}
        title="Notifications"
        requireUser
        allowLimited
        noTopBar
      />
      <Route
        exact
        path={["/messages", "/messages/:channelId"]}
        layout={RCAdminLayout}
        content={MerchantChatPage}
        title="Messages"
        requireUser
        allowLimited
        noTopBar
      />
      <Route
        exact
        path="/kanban"
        layout={RCAdminLayout}
        content={KanbanRequests}
        requireUser
        allowLimited
        requireFeature="kanban_list_view"
        /* Kanban renders its own <PageHeader>; without this the layout top bar
           would stack a second "Kanban" heading above it. */
        noTopBar
      />
      {/* Orders → Lookbooks. Before /requests/:owner, which would otherwise
          match "lookbooks" as an owner and render an empty Orders list. */}
      <Route
        exact
        path="/requests/lookbooks"
        layout={RCAdminLayout}
        content={RequestLookbooksPage}
        requireUser
        allowLimited
        requireFeature="request_lookbook_generation"
        noTopBar
      />
      {/* Back-compat: the old DAO-branded Orders path redirects to the
          renamed one. Placed before /requests/:owner so it wins the match,
          and `to` preserves any deep-link segments + query string. */}
      <RouterRoute
        path="/requests/dao"
        render={({ location }) => (
          <Redirect
            to={{
              ...location,
              pathname: location.pathname.replace(
                /^\/requests\/dao/,
                "/requests/orders"
              ),
            }}
          />
        )}
      />
      <Route
        exact
        path="/requests/:owner"
        layout={RCAdminLayout}
        content={Requests}
        requireUser
        allowLimited
        noTopBar
      />
      <Route
        exact
        path="/requests/:owner/:requestId?/:quoteId?/:tab?"
        layout={RCAdminLayout}
        content={Requests}
        requireUser
        allowLimited
        noTopBar
      />
      {/* withCategories dropped from this and the other SettingsTemplate-based routes below
          (shopify-settings, whatsapp-settings, whatsapp-coexistence, owner-admin,
          retail-circle-admin): it renders DefaultLayout's legacy <Categories> storefront
          nav (Shop/Order/Sell) + a divider above the page content. `Categories` already
          returns null whenever `isRetailCircle` is true (contexts/Settings.jsx), which is
          every deployment of this app (hostname always contains "retail-circle") — so the
          nav itself never rendered, but its empty wrapper div + the divider's extra
          `mt-3` margin still did, stacking a stray gap above SettingsTemplate's own
          PageHeader. Found 2026-08-31 auditing why these pages still passed a legacy-layout
          prop despite already being fully on Fontaine chrome. */}
      <Route
        exact
        path="/store-settings"
        content={DaoSettingsPage}
        requireAdmin
        allowLimited
        wideContent
      />
      {/* Back-compat: the old DAO-branded path redirects to the generalised one */}
      <RouterRoute exact path="/dao-settings">
        <Redirect to="/store-settings" />
      </RouterRoute>
      <Route
        exact
        path="/shopify-settings"
        content={ShopifySettingsPage}
        requireAdmin
        allowLimited
        wideContent
      />
      {/* WhatsApp settings tab removed 2026-10-29 — old links land on Store Settings */}
      <RouterRoute exact path="/whatsapp-settings">
        <Redirect to="/store-settings" />
      </RouterRoute>
      <Route
        exact
        path="/whatsapp-coexistence"
        content={WhatsAppCoexistencePage}
        requireAdmin
        requireMasterAdmin
        allowLimited
        wideContent
      />
      {/* Cooby VM WhatsApp Web sessions. The tab only shows for master admins and
          every endpoint is gated by master.admin server-side — the QR it renders
          links a device to a live WhatsApp account, so anyone who can see it can
          take the seat over. */}
      <Route
        exact
        path="/whatsapp-vms"
        content={CoobyVmPage}
        requireAdmin
        requireMasterAdmin
        allowLimited
        wideContent
      />
      {/* WhatsApp Broadcast. Owners/store admins and master admins (the page
          renders a permission panel for anyone else), and the store's plan
          must include the "WhatsApp Broadcast" row (wa_shared_number). */}
      <Route
        exact
        path="/whatsapp-broadcast"
        content={WaBroadcastPage}
        requireAdmin
        allowLimited
        wideContent
        requireFeature="wa_shared_number"
      />
      <Route
        exact
        path="/owner-admin"
        content={MasterAdminSettingsPage}
        requireAdmin
        allowLimited
        wideContent
      />
      {/* Legacy paths kept so existing links/bookmarks don't 404 after the
          renames: /master-admin-settings → /primary-admin → /owner-admin.
          Both old spellings still land on the current page. */}
      <Redirect exact from="/master-admin-settings" to="/owner-admin" />
      <Redirect exact from="/primary-admin" to="/owner-admin" />
      {/* Platform governance (MCP Access, Store Admin, Store Billing, Stripe
          Fee) — master admins only; the page itself denies non-masters and
          the backend gates every endpoint via EnsureMasterAdmin. This route
          was missing since the Primary/Master tab split, leaving masters
          with no UI for these sections. */}
      <Route
        exact
        path="/retail-circle-admin"
        content={MasterAdminGovernancePage}
        requireAdmin
        requireMasterAdmin
        allowLimited
        wideContent
      />
      {/* Same reasoning as above: the old governance path still resolves. */}
      <Redirect exact from="/master-admin" to="/retail-circle-admin" />
      <Route
        exact
        path="/pricing"
        withCategories
        content={PricingPage}
        requireAdmin
        allowLimited
        wideContent
      />
      <Route
        exact
        requireFeature="data_validation_screen"
        path="/ai-extraction"
        withCategories
        content={AiPricingView}
        requireAdmin
        allowLimited
        wideContent
      />
      {/* RC theme: /profile renders on RCAdminLayout, the same shell as
          /users/create, instead of the legacy DefaultLayout. `withCategories`
          + `title` are gone with it — they drew the legacy category header and
          the uppercase "MY PROFILE" bar; the page now supplies its own
          `.users-create-wrap__title`. Needs the same `noTopBar` /users/create
          already carries: Profile renders its own back arrow + "My Profile"
          heading, so RCAdminLayout's RCTopBar stacked a second, redundant one
          above it on desktop (RCTopBar's own "heading only on mobile" comment
          in RCAdmin/Layout.jsx was never backed by an actual desktop-hide CSS
          rule). Found 2026-08-30 sweep. */}
      <Route
        exact
        path="/profile/:action?"
        layout={RCAdminLayout}
        requireUser
        allowUnactivated
        content={Profile}
        allowShopify
        allowRetailCircle
        noTopBar
      />
      <Route
        exact
        path={
          "/request/:requestType/:requestId/calculation/:itemId?/:quoteId?/:tab?"
        }
        content={RequestCalculationPage}
        allowShopify
      />
      {/* V2 is now the default at /request/:type/:id. The /request/v2/...
          path stays as an alias so any external links / bookmarks
          created during the parallel-route phase keep working. */}
      <Route
        exact
        path={"/request/v2/:requestType/:requestId?/:quoteId?/:tab?"}
        layout={RCAdminLayout}
        content={RequestPageV2}
        noTopBar
        allowShopify
        merchantHomeLink="/listings"
      />
      <Route
        exact
        path={"/request/:requestType?/:requestId?/:quoteId?/:tab?"}
        layout={RCAdminLayout}
        content={RequestPageV2}
        noTopBar
        allowShopify
        merchantHomeLink="/listings"
      />
      <Route
        exact
        path={"/merchant-pricing-table"}
        content={MerchantPricingTable}
        allowRetailCircle
      />
      {/* Store Performance / Sales Rep / Executive View moved to the Dashboard
          (Dashboard v2, 2026-10-21). Old links and bookmarks land on the
          matching Dashboard tab. These MUST stay ahead of /analytics/:tab,
          which would otherwise match them first. */}
      <Redirect exact from="/analytics/store-performance" to="/dashboard?tab=store" />
      <Redirect exact from="/analytics/sales-rep" to="/dashboard?tab=day" />
      <Redirect exact from="/analytics/executive-view" to="/dashboard?tab=executive" />
      <Route exact path="/analytics" layout={RCAdminLayout} content={AnalyticsPage} requireMember noTopBar requireFeature="analytics_core" />
      <Route exact path="/analytics/:tab" layout={RCAdminLayout} content={AnalyticsPage} requireMember noTopBar requireFeature="analytics_core" />
      <Route exact path="/pnl" layout={RCAdminLayout} content={TransactionsPnlPage} requireMember noTopBar requireFeature="accounting_pnl_reconciliation" />
      <Route exact path="/reconciliation" layout={RCAdminLayout} content={ReconciliationPage} requireMember noTopBar requireFeature="accounting_pnl_reconciliation" />
      <Route exact path="/uptime" layout={RCAdminLayout} content={MonitoringPage} requireMember noTopBar requireFeature="uptime_monitor" />
      <Route exact path="/monitoring" layout={RCAdminLayout} content={MonitoringPage} requireMember noTopBar requireFeature="uptime_monitor" />
      {/* Master admins only — the pages render the permission state for anyone else. */}
      <Route exact path="/usage-analytics" layout={RCAdminLayout} content={UsageAnalyticsPage} requireMember noTopBar />
      {/* Deals — overnight buy opportunities, master admins only (the page gates itself). */}
      <Redirect exact from="/deals" to="/deals/private-sellers" />
      <Route exact path="/deals/:tab" layout={RCAdminLayout} content={DealsPage} requireMember noTopBar />
      <Route exact path="/usage-analytics/users/:id" layout={RCAdminLayout} content={UsageUserReportPage} requireMember noTopBar />

      {/* ── RC Admin pages ── */}
      {/* Dashboard — the KPI page, "Overview" at /rc/overview until 2026-10-20.
          Dashboard v2 (src/dashboard, 2026-10-21) replaced the Overview content.
          While core/config/dashboard.js LOCKS it, the URL redirects (replace,
          not push — so Back never bounces into it) to the sign-in landing page.
          The old URL follows it either way. */}
      <RouterRoute
        exact
        path="/rc/overview"
        render={({ location }) => <Redirect to={{ pathname: DASHBOARD_PATH, search: location.search }} />}
      />
      {DASHBOARD_LOCKED ? (
        <RouterRoute exact path={DASHBOARD_PATH}>
          <Redirect to={DASHBOARD_FALLBACK} />
        </RouterRoute>
      ) : (
        <Route
          exact
          path={DASHBOARD_PATH}
          layout={RCAdminLayout}
          content={DashboardPage}
          requireAdmin
          noTopBar
        />
      )}
      <Route
        exact
        path="/rc/listings"
        layout={RCAdminLayout}
        content={RCListingsPage}
        requireAdmin
        noTopBar
      />
      <Route
        exact
        path="/stock/import"
        layout={RCAdminLayout}
        content={ImportStock}
        requireAdmin
        noTopBar
      />
      <Route
        exact
        path="/stock/core-list"
        layout={RCAdminLayout}
        content={CoreListPage}
        requireAdmin
        noTopBar
        // The Stock Alert nav lands here. A store with only Geographic
        // Arbitrages gets through and CoreListTemplate sends it to that tab.
        requireFeature="core_inventory_management|geo_arbitrage"
      />
      {/* Ad List is the SAME list as Core List, shown for references whose
          listings are merchant-owned rather than stock or consignment — so it
          is the same page and the same feature gate, reading the tab off the
          path. Alex, 2026-10-06. */}
      <Route
        exact
        path="/stock/core-list/ad-list"
        layout={RCAdminLayout}
        content={CoreListPage}
        requireAdmin
        noTopBar
        requireFeature="core_inventory_management"
      />
      {/* Geographic Arbitrages & Sequencing — its own Feature Matrix row, so
          this tab does not need Stock alert. */}
      <Route
        exact
        path="/stock/core-list/arbitrages"
        layout={RCAdminLayout}
        content={CoreListPage}
        requireAdmin
        noTopBar
        requireFeature="geo_arbitrage"
      />
      {/* Sales Pipeline: watches created from order requests. A different
          list in the same table, so the same page and the same gate. */}
      <Route
        exact
        path="/stock/core-list/pipeline"
        layout={RCAdminLayout}
        content={CoreListPage}
        requireAdmin
        noTopBar
        requireFeature="core_inventory_management"
      />
      <Route
        exact
        path="/stock/core-list/insights"
        layout={RCAdminLayout}
        content={CoreListPage}
        requireAdmin
        noTopBar
        // Group Insights left the Feature Matrix (Alex, 2026-10-27); the tab
        // is withdrawn and the route sits under Stock alert like its API.
        requireFeature="core_inventory_management"
      />

      <Route
        exact
        path="/sign-in"
        layout={Authentication}
        content={SignIn}
        allowShopify
        allowRetailCircle
      />
      {/* Team invite acceptance (F3). requireUser stores afterLoginRedirect, so
          sign-in comes back here; allowUnactivated because an invitee is not
          approved until they accept. */}
      <Route
        exact
        path="/invite/:token"
        layout={Authentication}
        content={AcceptInvite}
        requireUser
        allowUnactivated
        allowRetailCircle
      />
      <Route
        exact
        path="/recover-account"
        layout={Authentication}
        layoutProps={{ noVideo: true }}
        content={RecoverAccount}
        requireGuest
        allowRetailCircle
        allowShopify
      />
      <Route
        exact
        path="/reset-password"
        layout={Authentication}
        layoutProps={{ noVideo: true }}
        content={ResetPassword}
        allowRetailCircle
      />
      <Route
        exact
        path="/recover-account/:code"
        layout={Authentication}
        layoutProps={{ noVideo: true }}
        content={ResetPassword}
        allowShopify
      />
      <Route
        exact
        path="/verify/:code?"
        layout={Authentication}
        layoutProps={{ noVideo: true }}
        content={VerifyEmail}
        allowRetailCircle
      />
      <Route
        exact
        path={"/offer-confirmation/:quote/verify/:code?"}
        content={OfferConfirmation}
        requireMember
      />
      /** Static Page List Routes */
      <Route
        exact
        path="/master-library"
        withCategories
        content={Pages}
        requireMember
        title="Master Library"
        type="library"
      />
      <Route
        exact
        path="/news"
        withCategories
        content={Pages}
        requireUser
        title="News"
        type="news"
      />
      <Route
        exact
        path="/events"
        withCategories
        content={Pages}
        requireUser
        type="event"
        allowShopify
      />
      {/* Legacy DAO Watches contact page — removed 2026-08-31, no longer served. */}
      <Redirect exact from="/contact" to="/error/404" />
      /** Static Page Route */
      <Route
        withCategories
        content={Page}
        className={userContext.user ? "" : "landing"}
      />
    </Switch>
  );
};

export default Routes;
